import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { Button, Modal, ModalTitle, ModalContent, ModalActions } from 'src/components/core';
import { Cross } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppDispatch } from 'src/hooks';
import {
    cancelAllConditionalOrders,
    cancelAllActiveOrders,
} from 'src/pages/spot/_redux/spotPageActions';
import { OrderType } from 'src/types';

interface ConfirmationModalProps {
    isOpen: boolean;
    orderType: OrderType;
    symbolId: string;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { typography, isMobile } }) => `
        .modal-paper {
            width:  447px;
            @media (max-width: 500px) { 
                width: 343px;
            }
            border-radius: 10px;
            .modal-title {
                padding: ${isMobile ? '16px' : '24px'};
                line-height: ${
                    isMobile
                        ? `${typography.lineHeight[4]}`
                        : `calc(${typography.lineHeight[7]} - 1px)`
                };
            }
        }
        .modal-content {
            padding: ${isMobile ? '14px 16px 16px' : '6px 24px 24px'};
        }
        .message {
            justify-content: space-between;
            align-items: center;
            ${isMobile ? `gap: 25px;` : ''}
        }
        .modal-actions {
            padding: 24px 24px 30px;
            gap: 16px;
            button {
                line-height: ${typography.lineHeight[4]};
                font-weight: ${typography.weight.medium};
                padding: 10px 52px;
            }
            @media (max-width: 500px) {
                justify-content: center;
                button {
                    padding: 10px 32px;
                }
            }
        }
    `}
`;

const LabelText = styled.div`
    ${({ theme: { colors, typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p2};
        line-height: calc(${typography.lineHeight[3]} - 0.5px);
        color: ${colors.text.secondary}
    `}
`;

const CancelAllOrdersConfirmModal: FC<ConfirmationModalProps> = ({
    isOpen,
    orderType,
    symbolId,
    handleClose,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { colors } = useTheme();

    const handleCancel = () => {
        handleClose();
    };

    const handleConfirm = () => {
        switch (orderType) {
            case 'ACTIVE':
                dispatch(cancelAllActiveOrders(symbolId === 'ALL' ? {} : { symbol_ids: symbolId }));
                break;
            case 'CONDITIONAL':
                dispatch(
                    cancelAllConditionalOrders(symbolId === 'ALL' ? {} : { symbol_id: symbolId }),
                );
                break;
        }
        handleClose();
    };

    return (
        <StyledModal size="sm" open={isOpen}>
            <ModalTitle>
                {t('cancel_confirmation')}
                <Cross
                    fill={colors.text.primary}
                    onClick={handleCancel}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                <FlexContainer className="message">
                    <LabelText>
                        {symbolId === 'ALL'
                            ? t(`cancel_all_${orderType.toLowerCase()}_orders_confirmation`)
                            : t(`cancel_symbol_${orderType.toLowerCase()}_orders_confirmation`, {
                                  symbolId: symbolId,
                              })}
                    </LabelText>
                </FlexContainer>
            </ModalContent>
            <ModalActions>
                <Button variant="outlined" onClick={handleCancel} color={colors.text.tertiary}>
                    {t('cancel')}
                </Button>
                <Button onClick={handleConfirm}>{t('confirm')}</Button>
            </ModalActions>
        </StyledModal>
    );
};

export { CancelAllOrdersConfirmModal };
